import { useMutation, useQuery } from '@apollo/client';
import {
    faArrowRotateRight,
    faExternalLink,
    faPencil,
    faPlus,
    faSpinnerThird,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../core/components/Icon.tsx';
import {
    DELETE_NETSUITE_INTEGRATION,
    GET_NETSUITE_INTEGRATIONS,
    TRIGGER_NETSUITE_INTEGRATION_MASTERDATA_UPDATE,
} from '../../queries.ts';
import {
    CUSTOMER_INTEGRATION_ERP_PATH,
    CUSTOMER_INTEGRATION_NETSUITE_ADD_PATH,
    CUSTOMER_INTEGRATION_NETSUITE_EDIT_PATH,
} from '../../constants.ts';
import { Link } from 'react-router-dom';
import { url } from '../../../core/utils/link.ts';
import { useToaster } from '../../../core/components/Toast.tsx';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page/index.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import { format } from 'date-fns';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import IntegrationBreadcrumb from '../../components/IntegrationBreadcrumb.tsx';

const AddIcon = withIcon(faPlus);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);
const RefreshIcon = withIcon(faArrowRotateRight);
const ExternalLinkIcon = withIcon(faExternalLink);
const LoadingIcon = withIcon(faSpinnerThird);

const NetsuiteRow = ({ integration, refetch }: { integration: any; refetch: any }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteIntegration] = useMutation(DELETE_NETSUITE_INTEGRATION, {
        refetchQueries: [GET_NETSUITE_INTEGRATIONS],
    });
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('netsuite.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('netsuite.deleteError'), status: 'error' });
        }
    };

    const [updateMasterDataIntegration] = useMutation(TRIGGER_NETSUITE_INTEGRATION_MASTERDATA_UPDATE);
    const handleUpdateMasterDataIntegration = async () => {
        try {
            await updateMasterDataIntegration({ variables: { id: integration.id } });
            await refetch();
            publishToast({ description: t('netsuite.updateMasterDataSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('netsuite.updateMasterDataError'), status: 'error' });
        }
    };

    const orderChannels = integration ? [...integration.orderChannels] : [];
    const rfqChannels = integration ? [...integration.rfqChannels] : [];
    const universalChannels = integration ? [...(integration.universalChannels || [])] : [];

    return (
        <Table.Row>
            <Table.Cell>{integration.name}</Table.Cell>
            <Table.Cell>
                <div className="flex gap-1.5 flex-wrap">
                    {orderChannels.map((channel) => (
                        <span
                            key={channel.id}
                            className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                        >
                            {channel.name}
                        </span>
                    ))}
                </div>
            </Table.Cell>
            <Table.Cell>
                <div className="flex gap-1.5 flex-wrap">
                    {rfqChannels.map((channel) => (
                        <span
                            key={channel.id}
                            className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                        >
                            {channel.name}
                        </span>
                    ))}
                </div>
            </Table.Cell>
            <Table.Cell>
                <div className="flex gap-1.5 flex-wrap">
                    {universalChannels.map((channel) => (
                        <span
                            key={channel.id}
                            className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                        >
                            {channel.name}
                        </span>
                    ))}
                </div>
            </Table.Cell>
            <Table.Cell>
                {integration.masterDataUpdateActive ? (
                    <span className="flex gap-2 items-center animate-pulse">
                        <span className="text-content-success-strong">
                            <LoadingIcon spin />
                        </span>
                        <span>
                            {`${t('netsuite.masterDataUpdateInProgress')} ${(integration.fetchProgress * 100).toFixed(2)}%`}
                        </span>
                    </span>
                ) : integration.masterDataUpdateError ? (
                    <div className="flex flex-col gap-1">
                        <span className="text-content-danger-strong">{t('netsuite.masterDataUpdateError')}</span>
                        <span className="text-content-medium text-sm whitespace-pre-line">
                            {integration.masterDataUpdateError}
                        </span>
                    </div>
                ) : (
                    <span>{format(new Date(integration.masterDataUpdatedAt), 'do MMMM yyyy, HH:mm')}</span>
                )}
            </Table.Cell>
            <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
            <Table.Cell sticky="right">
                <div className="flex gap-2">
                    <RowControlIconButton onClick={handleUpdateMasterDataIntegration}>
                        <RefreshIcon />
                    </RowControlIconButton>

                    {integration.instanceUrl && (
                        <RowControlIconButton asChild>
                            <Link
                                to={`${integration.instanceUrl}/app/center/card.nl`}
                                target="_blank"
                                className="!text-content-strong hover:!text-content-strong"
                            >
                                <ExternalLinkIcon />
                            </Link>
                        </RowControlIconButton>
                    )}

                    <RowControlIconButton asChild>
                        <Link
                            to={url(CUSTOMER_INTEGRATION_NETSUITE_EDIT_PATH, {
                                integrationId: integration.id,
                            })}
                            className="!text-content-strong hover:!text-content-strong"
                        >
                            <EditIcon />
                        </Link>
                    </RowControlIconButton>

                    <RowControlIconButton
                        className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                        onClick={handleDeleteIntegration}
                    >
                        <DeleteIcon />
                    </RowControlIconButton>
                </div>
            </Table.Cell>
        </Table.Row>
    );
};

const NetsuiteIntegrationPage = () => {
    const { t } = useTranslation('integrations');

    const { data, loading, refetch } = useQuery(GET_NETSUITE_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.netsuiteIntegrations || [];

    return (
        <Layout>
            <SettingsNavigation />
            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <IntegrationBreadcrumb
                        backTooltip={`${t('backTooltipText')} ${t('erp.pageTitle')}`}
                        items={[
                            { label: t('erp.pageTitle'), to: url(CUSTOMER_INTEGRATION_ERP_PATH) },
                            { label: t('netsuite.title') },
                        ]}
                    />

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <a href={t('netsuite.documentationLink')} target={'_blank'}>
                                <span className="flex gap-2 items-center">
                                    {t('netsuite.documentation')}
                                    <ExternalLinkIcon />{' '}
                                </span>
                            </a>
                        </Page.HeaderButton>

                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_NETSUITE_ADD_PATH)}>
                                <AddIcon />
                                {t('netsuite.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-80 w-80">{t('netsuite.table.name')}</Table.HeadCell>
                                <Table.HeadCell>{t('netsuite.table.orderChannels')}</Table.HeadCell>
                                <Table.HeadCell>{t('netsuite.table.rfqChannels')}</Table.HeadCell>
                                <Table.HeadCell>{t('netsuite.table.universalChannels')}</Table.HeadCell>
                                <Table.HeadCell>{t('netsuite.table.masterDataUpdatedAt')}</Table.HeadCell>
                                <Table.HeadCell>{t('netsuite.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <NetsuiteRow key={integration.id} integration={integration} refetch={refetch} />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={7} className="text-center text-content-medium">
                                        {t('netsuite.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>
        </Layout>
    );
};

export default NetsuiteIntegrationPage;
